<template>
    <div data-testid="price-histogram-filter">
        <h3>{{ $t('search.filters.price.DAILY') }}</h3>
        <Histogram
            id="price-filter"
            :min-bound="priceHistogram.min"
            :max-bound="priceHistogram.max"
            :data="priceHistogram.data"
            :selected-min="selectedFilters.minPrice"
            :selected-min-label="minLabel"
            :selected-max="selectedFilters.maxPrice"
            :selected-max-label="maxLabel"
            @input="onInput"
            @change="onChange"
        />
    </div>
</template>
<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {storeToRefs} from 'pinia';
    import {usePriceFormatter, useTranslation} from '#imports';
    import type {PriceHistogramFilter} from '@api';
    import Histogram from './Histogram.vue';
    import {useFiltersStore} from '../stores/filters.store';

    const filtersStore = useFiltersStore();
    const {selectedFilters} = storeToRefs(filtersStore);
    const {t} = useTranslation();

    const props = defineProps<{
        priceHistogram: PriceHistogramFilter;
    }>();

    const displayedMin = ref(selectedFilters.value.minPrice ?? props.priceHistogram.min);
    const displayedMax = ref(selectedFilters.value.maxPrice ?? props.priceHistogram.max);

    const {format: formatPrice} = usePriceFormatter(props.priceHistogram.currency);
    const minLabel = computed(() => {
        if (displayedMin.value > props.priceHistogram.min) {
            return formatPrice(displayedMin.value);
        }
        return t('search.filters.rate.min.label', [formatPrice(props.priceHistogram.min)]);
    });
    const maxLabel = computed(() => {
        if (displayedMax.value < props.priceHistogram.max) {
            return formatPrice(displayedMax.value);
        }
        return t('search.filters.rate.max.label', [formatPrice(props.priceHistogram.max)]);
    });

    function onInput(min: number, max: number) {
        displayedMin.value = min;
        displayedMax.value = max;
    }

    function onChange(selectedMin: number, selectedMax: number) {
        if (props.priceHistogram.min < selectedMin) {
            filtersStore.setPriceMinFilter(selectedMin);
        } else {
            filtersStore.setPriceMinFilter(undefined);
        }
        if (selectedMax < props.priceHistogram.max) {
            filtersStore.setPriceMaxFilter(selectedMax);
        } else {
            filtersStore.setPriceMaxFilter(undefined);
        }
    }
</script>
